import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import InputField from "../../../../../components/ui/InputField";
import Button from "../../../../../components/ui/Button";
import { RestaurantTheme } from "../../../../../types/restaurant";
import { Branch } from "../../../../../utils/supabaseClient";
import { ThemeType } from "../../../../../types/custom";
import { CustomTheme, ThemeProperties } from "../../../../../types/customTheme";
import * as themeService from "../../../../../services/themeService";
import ThemeFormModal from "./ThemeFormModal";

// Default theme palette - always available
const defaultPalette: ThemeProperties = {
	name: 'Default Theme',
	primaryColor: '#0ea5e9',
	secondaryColor: '#06b6d4',
	accentColor: '#3b82f6',
	backgroundColor: '#f8fafc',
	textColor: '#1e293b',
	cardColor: '#ffffff',
	borderColor: '#e2e8f0'
};

type ThemeSettingsProps = {
	restaurant: Branch | null;
	updateTheme: (updates: Partial<RestaurantTheme>) => void;
	branchId?: string;
}

const ThemeSettings: React.FC<ThemeSettingsProps> = ({
	restaurant, updateTheme, branchId
}) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	// State for custom themes
	const [customThemes, setCustomThemes] = useState<CustomTheme[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [editingTheme, setEditingTheme] = useState<CustomTheme | null>(null);
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

	// Fetch custom themes on mount
	useEffect(() => {
		if (branchId) {
			fetchThemes();
		}
	}, [branchId]);

	const fetchThemes = async () => {
		if (!branchId) return;
		setIsLoading(true);
		try {
			const themes = await themeService.getCustomThemes(branchId);
			setCustomThemes(themes);
		} catch (error) {
			console.error('Failed to fetch themes:', error);
		} finally {
			setIsLoading(false);
		}
	};

	// Apply a theme palette to the restaurant
	const applyTheme = (theme: ThemeProperties) => {
		updateTheme({
			primaryColor: theme.primaryColor,
			secondaryColor: theme.secondaryColor,
			accentColor: theme.accentColor,
			backgroundColor: theme.backgroundColor,
			textColor: theme.textColor,
			cardColor: theme.cardColor,
			borderColor: theme.borderColor
		});
	};

	// Handle saving a theme (create or update)
	const handleSaveTheme = async (themeData: ThemeProperties | (ThemeProperties & { id: string })) => {
		if (!branchId) return;
		setIsSaving(true);
		try {
			const themes = await themeService.upsertCustomThemes(branchId, [themeData]);
			setCustomThemes(themes);
			setShowModal(false);
			setEditingTheme(null);
		} catch (error) {
			console.error('Failed to save theme:', error);
		} finally {
			setIsSaving(false);
		}
	};

	// Handle deleting a theme
	const handleDeleteTheme = async (themeId: string) => {
		if (!branchId) return;
		setIsSaving(true);
		try {
			const remainingThemes = await themeService.deleteCustomTheme(branchId, themeId, customThemes);
			setCustomThemes(remainingThemes);
			setDeleteConfirmId(null);
		} catch (error) {
			console.error('Failed to delete theme:', error);
		} finally {
			setIsSaving(false);
		}
	};

	const openEditModal = (theme: CustomTheme) => {
		setEditingTheme(theme);
		setShowModal(true);
	};

	const openCreateModal = () => {
		setEditingTheme(null);
		setShowModal(true);
	};

	return <div className="space-y-6">
		{/* Theme Templates */}
		<Card>
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					{t('settings.customThemes')}
				</h3>
				<Button
					variant="primary"
					size="sm"
					leftIcon={<FiPlus />}
					onClick={openCreateModal}
				>
					{t('settings.addTheme')}
				</Button>
			</div>

			{isLoading ? (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{[1, 2, 3].map(i => (
						<div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg animate-pulse">
							<div className="flex items-center gap-3 mb-2">
								<div className="flex gap-1">
									<div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700" />
									<div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700" />
									<div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700" />
								</div>
								<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{/* Default Theme (always available) */}
					<button
						onClick={() => applyTheme(defaultPalette)}
						className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary transition-colors cursor-pointer text-start"
					>
						<div className="flex items-center gap-3">
							<div className="flex gap-1">
								<div
									className="w-4 h-4 rounded-full"
									style={{ backgroundColor: defaultPalette.primaryColor }}
								/>
								<div
									className="w-4 h-4 rounded-full"
									style={{ backgroundColor: defaultPalette.secondaryColor }}
								/>
								<div
									className="w-4 h-4 rounded-full"
									style={{ backgroundColor: defaultPalette.accentColor }}
								/>
							</div>
							<span className="text-sm font-medium text-gray-900 dark:text-white">
								{t('settings.defaultTheme')}
							</span>
						</div>
					</button>

					{/* Custom Themes from Database */}
					{customThemes.map((customTheme) => (
						<div
							key={customTheme.id}
							className="relative p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary transition-colors group"
						>
							<button
								onClick={() => applyTheme(customTheme.theme)}
								className="w-full text-start cursor-pointer"
							>
								<div className="flex items-center gap-3">
									<div className="flex gap-1">
										<div
											className="w-4 h-4 rounded-full"
											style={{ backgroundColor: customTheme.theme.primaryColor }}
										/>
										<div
											className="w-4 h-4 rounded-full"
											style={{ backgroundColor: customTheme.theme.secondaryColor }}
										/>
										<div
											className="w-4 h-4 rounded-full"
											style={{ backgroundColor: customTheme.theme.accentColor }}
										/>
									</div>
									<span className="text-sm font-medium text-gray-900 dark:text-white">
										{customTheme.theme.name}
									</span>
								</div>
							</button>

							{/* Edit/Delete Actions - Always visible on mobile, hover on desktop */}
							<div className="absolute top-2 end-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
								<button
									onClick={(e) => {
										e.stopPropagation();
										openEditModal(customTheme);
									}}
									className="p-2 sm:p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
									title={t('settings.editTheme')}
								>
									<FiEdit2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation();
										setDeleteConfirmId(customTheme.id);
									}}
									className="p-2 sm:p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
									title={t('settings.deleteTheme')}
								>
									<FiTrash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
								</button>
							</div>

							{/* Delete Confirmation */}
							{deleteConfirmId === customTheme.id && (
								<div className="absolute inset-0 bg-white dark:bg-gray-800 rounded-lg flex flex-col items-center justify-center gap-2 p-2">
									<p className="text-xs text-gray-600 dark:text-gray-400 text-center">
										{t('settings.themeDeleteConfirm')}
									</p>
									<div className="flex gap-2">
										<Button
											size="sm"
											variant="ghost"
											onClick={() => setDeleteConfirmId(null)}
										>
											{t('common.cancel')}
										</Button>
										<Button
											size="sm"
											variant="primary"
											className="bg-red-500 hover:bg-red-600"
											isLoading={isSaving}
											onClick={() => handleDeleteTheme(customTheme.id)}
										>
											{t('common.delete')}
										</Button>
									</div>
								</div>
							)}
						</div>
					))}

					{/* Empty State */}
					{customThemes.length === 0 && (
						<div className="col-span-full text-center py-6 text-gray-500 dark:text-gray-400">
							<p className="text-sm">{t('settings.noCustomThemes')}</p>
						</div>
					)}
				</div>
			)}
		</Card>

		{/* Custom Colors */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.customColors')}
			</h3>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.primaryColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.primaryColor ?? '#000000'}
							onChange={(e) => updateTheme({ primaryColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.primaryColor ?? '#000000'}
							onChange={(e) => updateTheme({ primaryColor: e.target.value })}
							placeholder="#0ea5e9"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.secondaryColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.secondaryColor ?? '#000000'}
							onChange={(e) => updateTheme({ secondaryColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.secondaryColor ?? '#000000'}
							onChange={(e) => updateTheme({ secondaryColor: e.target.value })}
							placeholder="#06b6d4"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.accentColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.accentColor ?? '#000000'}
							onChange={(e) => updateTheme({ accentColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.accentColor ?? '#000000'}
							onChange={(e) => updateTheme({ accentColor: e.target.value })}
							placeholder="#3b82f6"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.backgroundColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.backgroundColor ?? '#000000'}
							onChange={(e) => updateTheme({ backgroundColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.backgroundColor ?? '#000000'}
							onChange={(e) => updateTheme({ backgroundColor: e.target.value })}
							placeholder="#f8fafc"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.textColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.textColor ?? '#000000'}
							onChange={(e) => updateTheme({ textColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.textColor ?? '#000000'}
							onChange={(e) => updateTheme({ textColor: e.target.value })}
							placeholder="#1e293b"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.cardColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.cardColor ?? '#000000'}
							onChange={(e) => updateTheme({ cardColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.cardColor ?? '#000000'}
							onChange={(e) => updateTheme({ cardColor: e.target.value })}
							placeholder="#ffffff"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.borderColor')}
					</label>
					<div className="flex gap-2">
						<input
							type="color"
							value={(restaurant?.theme as ThemeType)?.borderColor ?? '#000000'}
							onChange={(e) => updateTheme({ borderColor: e.target.value })}
							className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
						/>
						<InputField
							value={(restaurant?.theme as ThemeType)?.borderColor ?? '#000000'}
							onChange={(e) => updateTheme({ borderColor: e.target.value })}
							placeholder="#e2e8f0"
							className={`flex-1 ${isRTL ? 'text-end' : ''}`}
							dir="ltr"
						/>
					</div>
				</div>
			</div>
		</Card>

		{/* Style Options */}
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.styleOptions')}
			</h3>
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.headerStyle')}
					</label>
					<div className="flex flex-wrap gap-2 sm:gap-3">
						{(['transparent', 'solid', 'cover'] as const).map((style) => (
							<button
								key={style}
								onClick={() => updateTheme({ headerStyle: style })}
								className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.headerStyle === style
									? 'border-primary bg-primary/10 text-primary'
									: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
									}`}
							>
								{t(`settings.headerStyles.${style}`)}
							</button>
						))}
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
						{t('settings.cardStyle')}
					</label>
					<div className="flex flex-wrap gap-2 sm:gap-3">
						{(['minimal', 'shadow', 'bordered'] as const).map((style) => (
							<button
								key={style}
								onClick={() => updateTheme({ cardStyle: style })}
								className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.cardStyle === style
									? 'border-primary bg-primary/10 text-primary'
									: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
									}`}
							>
								{t(`settings.cardStyles.${style}`)}
							</button>
						))}
					</div>
				</div>
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">	{t('settings.preview.addToCart')}</label>
					<div className="flex flex-wrap gap-2 sm:gap-3">
						{[true, false].map((value) => (
							<button
								key={value ? 'yes' : 'no'}
								onClick={() => updateTheme({ isAddToCart: value })}
								className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-md border transition-colors cursor-pointer ${(restaurant?.theme as ThemeType)?.isAddToCart === value
									? 'border-primary bg-primary/10 text-primary'
									: 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
									}`}
							>
								{t(`settings.addToCartOptions.${value ? 'yes' : 'no'}`)}
							</button>
						))}
					</div>
				</div>
			</div>
		</Card>

		{/* Theme Form Modal */}
		<ThemeFormModal
			isOpen={showModal}
			onClose={() => {
				setShowModal(false);
				setEditingTheme(null);
			}}
			onSave={handleSaveTheme}
			theme={editingTheme}
			isLoading={isSaving}
		/>
	</div>
};

export default ThemeSettings;